// Where the room is solid. The presenter never reads a room's furniture by
// name, so it cannot know that a wall stands behind a shelf or a lamp hangs
// over the bench. It reads the room's own triangles instead: every one is
// sampled into a grid of small cells, and a live object asks the grid before
// it turns or flies. A path or a pose is free when none of the object's
// sample points lies in a solid cell it was not already touching at rest.
//
// The room's model is surfaces, not volumes, so the grid holds a skin: each
// sample marks the cells half a cell to either side of its surface. A skin
// two cells thick cannot slip between an object's sample points, which lie
// no further apart than one cell. The price is a margin: an object counts as
// touching from up to a cell and a half away.

import * as THREE from "three";

/** Edge of one cell in metres. */
export const CELL = 0.015;

export class Clearance {
  private cells: Uint8Array;
  private origin: THREE.Vector3;
  private count: [number, number, number];

  /** Sample every triangle of `room` that reaches into `box`. */
  constructor(room: THREE.Object3D, box: THREE.Box3) {
    this.origin = box.min.clone();
    const size = box.getSize(new THREE.Vector3());
    this.count = [Math.ceil(size.x / CELL), Math.ceil(size.y / CELL), Math.ceil(size.z / CELL)];
    this.cells = new Uint8Array(this.count[0] * this.count[1] * this.count[2]);

    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const c = new THREE.Vector3();
    const ab = new THREE.Vector3();
    const ac = new THREE.Vector3();
    const point = new THREE.Vector3();
    const normal = new THREE.Vector3();
    const side = new THREE.Vector3();
    const reach = new THREE.Box3();
    room.updateMatrixWorld(true);
    room.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const position = mesh.geometry.getAttribute("position");
      const index = mesh.geometry.getIndex();
      const corners = index ? index.count : position.count;
      for (let i = 0; i < corners; i += 3) {
        a.fromBufferAttribute(position, index ? index.getX(i) : i).applyMatrix4(mesh.matrixWorld);
        b.fromBufferAttribute(position, index ? index.getX(i + 1) : i + 1).applyMatrix4(mesh.matrixWorld);
        c.fromBufferAttribute(position, index ? index.getX(i + 2) : i + 2).applyMatrix4(mesh.matrixWorld);
        if (!reach.setFromPoints([a, b, c]).intersectsBox(box)) continue;
        // Points across the triangle no further apart than half a cell.
        ab.subVectors(b, a);
        ac.subVectors(c, a);
        normal.crossVectors(ab, ac);
        if (normal.lengthSq() === 0) continue;
        normal.normalize().multiplyScalar(CELL * 0.5);
        const steps = Math.max(1, Math.ceil(Math.max(ab.length(), ac.length(), b.distanceTo(c)) / (CELL * 0.5)));
        for (let u = 0; u <= steps; u++) {
          for (let v = 0; u + v <= steps; v++) {
            point.copy(a).addScaledVector(ab, u / steps).addScaledVector(ac, v / steps);
            for (const k of [-1, 0, 1]) {
              const at = this.cellOf(side.copy(point).addScaledVector(normal, k));
              if (at >= 0) this.cells[at] = 1;
            }
          }
        }
      }
    });
  }

  /** Index of the cell holding `point`, or -1 outside the grid. */
  cellOf(point: THREE.Vector3): number {
    const x = Math.floor((point.x - this.origin.x) / CELL);
    const y = Math.floor((point.y - this.origin.y) / CELL);
    const z = Math.floor((point.z - this.origin.z) / CELL);
    const [nx, ny, nz] = this.count;
    if (x < 0 || y < 0 || z < 0 || x >= nx || y >= ny || z >= nz) return -1;
    return (z * ny + y) * nx + x;
  }

  /** The solid cells among `points`: what an object standing there touches. */
  touching(points: readonly THREE.Vector3[]): Set<number> {
    const touched = new Set<number>();
    for (const point of points) {
      const at = this.cellOf(point);
      if (at >= 0 && this.cells[at]) touched.add(at);
    }
    return touched;
  }

  /**
   * The solid cells in a flat slab around `centre`: within `radius` of it on
   * the ground plane and between `below` and `above` it in height. This is
   * the surface something stands on, and its skin.
   */
  slab(centre: THREE.Vector3, radius: number, below: number, above: number): Set<number> {
    return this.within(new THREE.Box3(
      new THREE.Vector3(centre.x - radius, centre.y - below, centre.z - radius),
      new THREE.Vector3(centre.x + radius, centre.y + above, centre.z + radius),
    ));
  }

  /** The solid cells that `box` reaches into. */
  within(box: THREE.Box3): Set<number> {
    const found = new Set<number>();
    const [nx, ny, nz] = this.count;
    const span = (low: number, high: number, origin: number, limit: number): [number, number] =>
      [Math.max(0, Math.floor((low - origin) / CELL)), Math.min(limit - 1, Math.floor((high - origin) / CELL))];
    const [x0, x1] = span(box.min.x, box.max.x, this.origin.x, nx);
    const [y0, y1] = span(box.min.y, box.max.y, this.origin.y, ny);
    const [z0, z1] = span(box.min.z, box.max.z, this.origin.z, nz);
    for (let z = z0; z <= z1; z++) {
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const at = (z * ny + y) * nx + x;
          if (this.cells[at]) found.add(at);
        }
      }
    }
    return found;
  }

  /** True when none of `points` lies in a solid cell outside `allowed`. */
  free(points: readonly THREE.Vector3[], allowed: ReadonlySet<number>): boolean {
    for (const point of points) {
      const at = this.cellOf(point);
      if (at >= 0 && this.cells[at] && !allowed.has(at)) return false;
    }
    return true;
  }
}

/** Points through a box no further apart than a cell, faces included. */
export function lattice(box: THREE.Box3): THREE.Vector3[] {
  const size = box.getSize(new THREE.Vector3());
  const n = [size.x, size.y, size.z].map((length) => Math.max(1, Math.ceil(length / CELL)));
  const points: THREE.Vector3[] = [];
  for (let i = 0; i <= n[0]!; i++) {
    for (let j = 0; j <= n[1]!; j++) {
      for (let k = 0; k <= n[2]!; k++) {
        points.push(new THREE.Vector3(box.min.x + (size.x * i) / n[0]!, box.min.y + (size.y * j) / n[1]!, box.min.z + (size.z * k) / n[2]!));
      }
    }
  }
  return points;
}
